import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Download,
  FileWarning,
  Loader2,
  PlayCircle,
  ShieldCheck,
  Sparkles,
} from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import MainHeader from '@/components/layout/MainHeader'
import { Main } from '@/components/main'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/screenata/readiness',
)({
  component: ReadinessPage,
})

type QueueTab = 'required' | 'optional' | 'gaps' | 'approvals'

type TestOutlineItem = {
  statusId: string
  title: string
  status: string
  category: 'policy' | 'test'
  controlRefs: string[]
  necessity: 'required' | 'common-ask' | 'scope-dependent' | 'optional'
  necessitySource?: 'type-i-baseline' | 'auditor-ask' | 'scope-selected' | null
  isNA: boolean
}

type TestOutlineControl = {
  controlRef: string
  tests: TestOutlineItem[]
}

type Blocker = {
  control: string
  reason: string
  severity: string
}

const STATUS_RANK: Record<string, number> = {
  FAILED: 0,
  NEEDS_REVIEW: 1,
  IN_PROGRESS: 2,
  NOT_STARTED: 3,
  NOT_APPLICABLE: 4,
  APPROVED: 5,
}

function ReadinessTrendChart({ data }: { data: Array<{ overall: number; snapshotAt: string }> }) {
  if (data.length < 2) return null

  const width = 600
  const height = 160
  const padding = { top: 10, right: 10, bottom: 30, left: 40 }
  const chartWidth = width - padding.left - padding.right
  const chartHeight = height - padding.top - padding.bottom

  const xScale = (i: number) => padding.left + (i / (data.length - 1)) * chartWidth
  const yScale = (val: number) => padding.top + chartHeight - (val / 100) * chartHeight

  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.overall)}`).join(' ')
  const areaPath = `${linePath} L ${xScale(data.length - 1)} ${yScale(0)} L ${xScale(0)} ${yScale(0)} Z`

  const labelIndices = [0, Math.floor(data.length / 2), data.length - 1]

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="h-auto w-full">
      {[0, 25, 50, 75, 100].map((v) => (
        <g key={v}>
          <line
            x1={padding.left}
            y1={yScale(v)}
            x2={width - padding.right}
            y2={yScale(v)}
            stroke="#e5e7eb"
            strokeDasharray="4 4"
          />
          <text
            x={padding.left - 6}
            y={yScale(v) + 4}
            textAnchor="end"
            className="fill-muted-foreground"
            fontSize="10"
          >
            {v}%
          </text>
        </g>
      ))}
      <path d={areaPath} fill="rgb(16 185 129 / 0.1)" />
      <path d={linePath} fill="none" stroke="rgb(16 185 129)" strokeWidth="2" />
      {data.map((d, i) => (
        <circle key={i} cx={xScale(i)} cy={yScale(d.overall)} r="3" fill="rgb(16 185 129)" />
      ))}
      {labelIndices.map((i) => (
        <text
          key={i}
          x={xScale(i)}
          y={height - 5}
          textAnchor="middle"
          className="fill-muted-foreground"
          fontSize="10"
        >
          {new Date(data[i].snapshotAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        </text>
      ))}
    </svg>
  )
}

function ReadinessPage() {
  const { workspaceId } = useParams({
    from: '/(protected)/workspaces/$workspaceId/screenata/readiness',
  })
  const [programId, setProgramId] = useState<string | null>(null)
  const [queueTab, setQueueTab] = useState<QueueTab>('required')
  const [isRunningCheck, setIsRunningCheck] = useState(false)
  const queryClient = useQueryClient()

  const exportMutation = useMutation(trpc.complianceProgram.exportMatrix.mutationOptions())
  const consistencyMutation = useMutation(trpc.complianceProgram.runConsistencyCheck.mutationOptions())
  const finalizeMutation = useMutation(trpc.complianceProgram.finalizeTestPlan.mutationOptions())
  const [isFinalized, setIsFinalized] = useState(false)

  const handleExportMatrix = async () => {
    if (!programId) return
    try {
      const result = await exportMutation.mutateAsync({ programId })
      const byteCharacters = atob(result.data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: result.mimeType })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = result.filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Export failed:', error)
    }
  }

  const handleRunConsistencyCheck = async () => {
    if (!programId) return
    setIsRunningCheck(true)
    try {
      await consistencyMutation.mutateAsync({ programId })
      queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getReadiness.queryKey() })
    } catch (error) {
      console.error('Consistency check failed:', error)
    } finally {
      setIsRunningCheck(false)
    }
  }

  const programQuery = useQuery(
    trpc.complianceProgram.getProgram.queryOptions({ framework: 'SOC2' }),
  )

  useEffect(() => {
    if (programQuery.data?.id) {
      setProgramId(programQuery.data.id)
    }
    if (programQuery.data?.testPlanPhase === 'complete') {
      setIsFinalized(true)
    }
  }, [programQuery.data])

  const isTriageEnabled = programQuery.data?.claimTriageEnabled ?? false
  const needsFinalize = isTriageEnabled && !isFinalized

  const handleFinalize = async () => {
    if (!programId) return
    try {
      const result = await finalizeMutation.mutateAsync({ programId })
      setIsFinalized(true)
      queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getReadiness.queryKey() })
      queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getTestPlanOutline.queryKey() })
      queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getProgram.queryKey() })
    } catch (error) {
      console.error('Finalize failed:', error)
    }
  }

  const readinessQuery = useQuery({
    ...trpc.complianceProgram.getReadiness.queryOptions({ programId: programId! }),
    enabled: !!programId,
  })

  const trendQuery = useQuery({
    ...trpc.complianceProgram.getReadinessTrend.queryOptions({ programId: programId!, days: 90 }),
    enabled: !!programId,
  })

  const pendingApprovalsQuery = useQuery({
    ...trpc.complianceProgram.getPendingApprovals.queryOptions({ programId: programId! }),
    enabled: !!programId,
  })

  const outlineQuery = useQuery({
    ...trpc.complianceProgram.getTestPlanOutline.queryOptions({ programId: programId! }),
    enabled: !!programId,
  })

  const readiness = readinessQuery.data
  const pendingApprovals = pendingApprovalsQuery.data?.items ?? []

  const statusLabel = (status: string) => {
    if (status === 'APPROVED') return 'Approved'
    if (status === 'FAILED') return 'Failed'
    if (status === 'NEEDS_REVIEW') return 'In review'
    if (status === 'IN_PROGRESS') return 'In progress'
    if (status === 'NOT_APPLICABLE') return 'Not applicable'
    return 'Not started'
  }

  const statusTone = (status: string) => {
    if (status === 'APPROVED') return 'bg-emerald-100 text-emerald-800 border-emerald-200'
    if (status === 'FAILED') return 'bg-red-100 text-red-800 border-red-200'
    if (status === 'NEEDS_REVIEW') return 'bg-amber-100 text-amber-800 border-amber-200'
    if (status === 'IN_PROGRESS') return 'bg-blue-100 text-blue-800 border-blue-200'
    if (status === 'NOT_APPLICABLE') return 'bg-slate-100 text-slate-700 border-slate-200'
    return 'bg-muted text-muted-foreground border-border'
  }

  const severityTone = (severity: string) => {
    if (severity === 'critical') return 'bg-red-100 text-red-800 border-red-200'
    if (severity === 'high') return 'bg-amber-100 text-amber-800 border-amber-200'
    if (severity === 'medium') return 'bg-blue-100 text-blue-800 border-blue-200'
    return 'bg-slate-100 text-slate-700 border-slate-200'
  }

  const sourceLabel = (source: TestOutlineItem['necessitySource']) => {
    if (source === 'type-i-baseline') return 'Type I baseline'
    if (source === 'auditor-ask') return 'Auditor ask'
    if (source === 'scope-selected') return 'Scope-selected'
    return 'Standard'
  }

  const queueData = useMemo(() => {
    const controls = (outlineQuery.data?.controls ?? []) as TestOutlineControl[]
    const map = new Map<string, TestOutlineItem & { controlRef: string }>()

    for (const control of controls) {
      for (const test of control.tests ?? []) {
        if (test.isNA) continue
        if (!map.has(test.statusId)) {
          map.set(test.statusId, { ...test, controlRef: control.controlRef })
        }
      }
    }

    const all = Array.from(map.values()).sort((a, b) => {
      const aRank = STATUS_RANK[a.status] ?? 10
      const bRank = STATUS_RANK[b.status] ?? 10
      if (aRank !== bRank) return aRank - bRank
      return a.title.localeCompare(b.title)
    })

    const required = all.filter((item) => item.necessity === 'required')
    const optional = all.filter((item) => item.necessity !== 'required')

    return { required, optional }
  }, [outlineQuery.data?.controls])

  if (!programId) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        No compliance program found. Start from the chat page.
      </div>
    )
  }

  if (!readiness) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        Loading readiness data...
      </div>
    )
  }

  const blockers = (readiness.blockers ?? []) as Blocker[]
  const counters = readiness.counters
  const requiredApplicableTotal = Math.max(
    0,
    (counters?.requiredTestsTotal ?? 0) - (counters?.requiredTestsNA ?? 0),
  )
  const requiredApproved = counters?.requiredTestsApproved ?? 0
  const requiredProgress = requiredApplicableTotal > 0
    ? Math.round((requiredApproved / requiredApplicableTotal) * 100)
    : 0

  return (
    <Main>
      <MainHeader className="!mb-0 pr-4">
        <div className="flex w-full items-center gap-2">
          <span className="text-sm font-semibold">Readiness Command Center</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              onClick={handleExportMatrix}
              className="inline-flex h-8 items-center gap-2 rounded-md border bg-background px-3 text-sm font-medium transition-colors hover:bg-muted"
            >
              <Download className="h-4 w-4" />
              Export auditor pack
            </button>
            <button
              onClick={handleRunConsistencyCheck}
              disabled={isRunningCheck}
              className="inline-flex h-8 items-center gap-2 rounded-md bg-foreground px-3 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {isRunningCheck
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <PlayCircle className="h-4 w-4" />}
              {isRunningCheck ? 'Running...' : 'Run consistency check'}
            </button>
          </div>
        </div>
      </MainHeader>

      <div className="h-[calc(100vh-50px)] w-full border-t">
        <div className="h-full overflow-y-auto">
          <div className="w-full space-y-6 p-6">
            {needsFinalize && (
              <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50/50 px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-blue-900">Test plan not yet finalized</p>
                  <p className="text-xs text-blue-700">
                    Claims have been triaged. Finalize the test plan to create evidence collection tasks from resolved claims.
                  </p>
                </div>
                <button
                  onClick={handleFinalize}
                  disabled={finalizeMutation.isPending}
                  className="inline-flex h-8 items-center gap-2 rounded-md bg-blue-600 px-4 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
                >
                  {finalizeMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ShieldCheck className="h-4 w-4" />
                  )}
                  {finalizeMutation.isPending ? 'Finalizing...' : 'Finalize Test Plan'}
                </button>
              </div>
            )}

            {isTriageEnabled && isFinalized && (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/50 px-4 py-3">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <p className="text-sm text-emerald-700">
                  Test plan finalized — evidence collection tasks have been created from resolved claims.
                </p>
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-xl border bg-card p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Type I readiness</div>
                <div className="mt-2 flex items-end gap-2">
                  <div className="text-3xl font-semibold text-emerald-600">{readiness.overall}%</div>
                  <span className="mb-1 text-xs text-muted-foreground">overall</span>
                </div>
              </div>
              <div className="rounded-xl border bg-card p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Required pack progress</div>
                <div className="mt-2 flex items-end gap-2">
                  <div className="text-3xl font-semibold">{requiredProgress}%</div>
                  <span className="mb-1 text-xs text-muted-foreground">
                    {requiredApproved}/{requiredApplicableTotal} approved
                  </span>
                </div>
              </div>
              <div className="rounded-xl border bg-card p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Evidence gaps</div>
                <div className="mt-2 flex items-end gap-2">
                  <div className="text-3xl font-semibold text-amber-600">{blockers.length}</div>
                  <span className="mb-1 text-xs text-muted-foreground">open blockers</span>
                </div>
              </div>
              <div className="rounded-xl border bg-card p-4">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Approvals pending</div>
                <div className="mt-2 flex items-end gap-2">
                  <div className="text-3xl font-semibold text-blue-600">{pendingApprovals.length}</div>
                  <span className="mb-1 text-xs text-muted-foreground">requires review</span>
                </div>
              </div>
            </div>
            <div className="grid gap-4 xl:grid-cols-3">
              <div className="rounded-xl border bg-card p-5 xl:col-span-2">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Readiness trend</h2>
                    <p className="text-xs text-muted-foreground">Last 90 days</p>
                  </div>
                  <div className="inline-flex items-center gap-2 rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">
                    <Sparkles className="h-3.5 w-3.5" />
                    Live readiness snapshots
                  </div>
                </div>
                {trendQuery.data && trendQuery.data.length >= 2 ? (
                  <ReadinessTrendChart data={trendQuery.data} />
                ) : (
                  <div className="flex h-[160px] items-center justify-center rounded-lg border border-dashed text-sm text-muted-foreground">
                    Trend appears after at least 2 snapshots.
                  </div>
                )}
              </div>

              <div className="rounded-xl border bg-card p-5">
                <h2 className="text-lg font-semibold">Category scores</h2>
                <div className="mt-4 space-y-3">
                  {Object.entries(readiness.categories).map(([key, value]) => (
                    <div key={key}>
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="font-medium capitalize text-muted-foreground">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <span className="font-semibold text-foreground">{value}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-muted">
                        <div
                          className="h-2 rounded-full bg-emerald-600 transition-all"
                          style={{ width: `${value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-xl border bg-card">
              <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Action queues</h2>
                  <p className="text-xs text-muted-foreground">Operate by required pack first, then optional and approvals.</p>
                </div>
                <Tabs value={queueTab} onValueChange={(v) => setQueueTab(v as QueueTab)}>
                  <TabsList className="grid h-9 grid-cols-2 md:grid-cols-4">
                    <TabsTrigger value="required">Required pack ({queueData.required.length})</TabsTrigger>
                    <TabsTrigger value="optional">Optional checks ({queueData.optional.length})</TabsTrigger>
                    <TabsTrigger value="gaps">Evidence gaps ({blockers.length})</TabsTrigger>
                    <TabsTrigger value="approvals">Approvals ({pendingApprovals.length})</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

        {queueTab === 'required' && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Item</th>
                  <th className="px-4 py-2 text-left">Control</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Source</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {queueData.required.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                      No required checklist items yet.
                    </td>
                  </tr>
                ) : (
                  queueData.required.map((item) => (
                    <tr key={item.statusId} className="border-t">
                      <td className="px-4 py-3">
                        <div className="font-medium">{item.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {item.category === 'policy' ? 'Policy' : 'Checklist test'}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{item.controlRef}</td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium', statusTone(item.status))}>
                          {statusLabel(item.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{sourceLabel(item.necessitySource)}</td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to="/workspaces/$workspaceId/screenata/tests"
                          params={{ workspaceId }}
                          className="inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted"
                        >
                          Open checklist
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {queueTab === 'optional' && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Item</th>
                  <th className="px-4 py-2 text-left">Control</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Necessity</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {queueData.optional.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                      No optional checks in the current checklist.
                    </td>
                  </tr>
                ) : (
                  queueData.optional.map((item) => (
                    <tr key={item.statusId} className="border-t">
                      <td className="px-4 py-3 font-medium">{item.title}</td>
                      <td className="px-4 py-3 font-mono text-xs">{item.controlRef}</td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium', statusTone(item.status))}>
                          {statusLabel(item.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {item.necessity === 'common-ask'
                          ? 'Common ask'
                          : item.necessity === 'scope-dependent'
                            ? 'Scope-dependent'
                            : 'Optional'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to="/workspaces/$workspaceId/screenata/tests"
                          params={{ workspaceId }}
                          className="inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted"
                        >
                          Open checklist
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {queueTab === 'gaps' && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Control</th>
                  <th className="px-4 py-2 text-left">Gap</th>
                  <th className="px-4 py-2 text-left">Severity</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {blockers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-emerald-700">
                      No blockers detected.
                    </td>
                  </tr>
                ) : (
                  blockers.map((blocker, idx) => (
                    <tr key={`${blocker.control}-${idx}`} className="border-t">
                      <td className="px-4 py-3 font-mono text-xs">{blocker.control}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          <FileWarning className="mt-0.5 h-4 w-4 text-amber-600" />
                          <span>{blocker.reason}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium', severityTone(blocker.severity))}>
                          {blocker.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to="/workspaces/$workspaceId/screenata/tests"
                          params={{ workspaceId }}
                          className="inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted"
                        >
                          Resolve in checklist
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {queueTab === 'approvals' && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Item</th>
                  <th className="px-4 py-2 text-left">Type</th>
                  <th className="px-4 py-2 text-left">Severity</th>
                  <th className="px-4 py-2 text-left">Created</th>
                  <th className="px-4 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {pendingApprovals.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                      No pending approvals.
                    </td>
                  </tr>
                ) : (
                  pendingApprovals.map((item) => (
                    <tr key={item.id} className="border-t">
                      <td className="px-4 py-3">
                        <div className="font-medium">{item.title}</div>
                        <div className="text-xs text-muted-foreground">{item.description}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{item.type.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium', severityTone(item.severity))}>
                          {item.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        <div className="inline-flex items-center gap-1.5">
                          <Clock3 className="h-3.5 w-3.5" />
                          {new Date(item.createdAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link
                          to="/workspaces/$workspaceId/screenata/policies"
                          params={{ workspaceId }}
                          className="inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium hover:bg-muted"
                        >
                          Review approvals
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-lg border bg-card p-4">
                <div className="mb-1 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  Evidence freshness
                </div>
                <div className="text-sm text-muted-foreground">
                  {readiness.evidence?.fresh ?? 0} fresh, {readiness.evidence?.stale ?? 0} stale, {readiness.evidence?.expired ?? 0} expired
                </div>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="mb-1 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Remediation
                </div>
                <div className="text-sm text-muted-foreground">
                  {counters?.openTickets ?? 0} open tickets, {counters?.resolvedTickets ?? 0} resolved
                </div>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="mb-1 inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <ShieldCheck className="h-4 w-4 text-blue-600" />
                  Claims coverage
                </div>
                <div className="text-sm text-muted-foreground">
                  {readiness.categories.claimCoverage}% controls mapped to supporting claims
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Main>
  )
}
